import { InformativeNotification } from '@agilelab/plugin-wb-notification-common';
import { CodeSnippet } from '@mainstage/core-components';
import { WbAvatar } from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { IconButton, Typography, useTheme } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NotificationsIcon from '@material-ui/icons/Notifications';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { useCallback, useId } from 'react';
import { MarkAsReadButton } from './MarkAsReadButton';
import {
  NotificationDisclosure,
  NotificationDisclosureDetails,
} from './NotificationDisclosure';
import { useNotificationStyle } from './NotificationsPageStyle';
import logo from './witboost_logo.svg';

const RenderHTML = (props: { HTML: string }) => {
  if (props.HTML)
    return (
      <div
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.HTML) }}
      />
    );
  return <></>;
};

const RenderJSON = (props: { JSON: string }) => {
  const theme = useTheme();
  if (props.JSON)
    try {
      return (
        <CodeSnippet
          text={JSON.stringify(props.JSON, null, 2)}
          language="json"
          customStyle={{
            padding: '16px',
            margin: '0',
            maxHeight: '200px',
            backgroundColor: theme.palette.background.default,
          }}
        />
      );
    } catch (error) {
      return <Typography color="error">Cannot Parse JSON Response</Typography>;
    }
  return <></>;
};

export const InformativeNotificationDisclosure = (props: {
  notification: InformativeNotification;
  onAccordionExpansionsChange: (value: string | boolean) => void;
  expandedAccordion: string | boolean;
}) => {
  const { notification, onAccordionExpansionsChange, expandedAccordion } =
    props;
  const classes = useNotificationStyle();
  const configApi = useApi(configApiRef);
  const detailsRegionId = useId();

  const expandable =
    !!notification.notification_request.html ||
    !!notification.notification_request.json;

  const isExpanded = expandedAccordion === notification.id.toString();

  const onToggle = useCallback(() => {
    if (!expandable) {
      return;
    }
    onAccordionExpansionsChange(
      isExpanded ? false : notification.id.toString(),
    );
  }, [expandable, isExpanded, notification.id, onAccordionExpansionsChange]);

  const summaryContent = (
    <div className={classes.summaryDescription}>
      <WbAvatar
        displayName={
          notification.sender ??
          configApi.getOptionalString('app.title') ??
          'Witboost'
        }
        picture={logo}
        classes={{ avatar: classes.avatar }}
      />
      <div className={classes.summaryDescriptionText}>
        <Typography variant="body1">
          <RenderHTML HTML={notification.notification_request.text} />
        </Typography>
      </div>
    </div>
  );

  const notificationHeader = (
    <div className={classes.expandableSummaryHeaderOutsideDisclosure}>
      <div className={classes.summaryHeader}>
        <Typography className={classes.notificationTime}>
          {DateTime.fromISO(notification.created_at).toFormat(
            'dd/MM/yyyy HH:mm',
          )}
        </Typography>
        <NotificationsIcon fontSize="small" color="primary" />
      </div>
      <div className={classes.expandableSummaryHeaderActions}>
        <MarkAsReadButton notification={notification} />
        {expandable && (
          <IconButton
            onClick={onToggle}
            size="small"
            aria-expanded={isExpanded}
            aria-controls={detailsRegionId}
            aria-label="toggle notification details"
            className={`${classes.disclosureToggleButton} ${
              isExpanded ? classes.disclosureToggleButtonExpanded : ''
            }`}
          >
            <ExpandMoreIcon color="primary" />
          </IconButton>
        )}
      </div>
    </div>
  );

  return (
    <div className={classes.expandableDisclosureContainer}>
      {notificationHeader}
      <NotificationDisclosure
        expanded={expandable && isExpanded}
        style={{
          border: 'none',
          borderBottom: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <div className={classes.expandableSummaryBody}>
          <div className={classes.expandableSummaryBodyContent}>
            {summaryContent}
          </div>
        </div>
        {expandable && (
          <NotificationDisclosureDetails
            id={detailsRegionId}
            style={{ flexDirection: 'column' }}
          >
            <RenderHTML HTML={notification.notification_request.html} />
            <div style={{ maxWidth: '100%' }}>
              <RenderJSON JSON={notification.notification_request.json} />
            </div>
          </NotificationDisclosureDetails>
        )}
      </NotificationDisclosure>
    </div>
  );
};
